/**
 * The single place this app talks to the network.
 *
 * Responsibilities, all in interceptors so no caller has to remember them:
 *
 * * **Language** -- sent twice, on purpose. `?lang=` drives which bilingual
 *   column the server resolves for display; `Accept-Language` drives the
 *   language of server-side validation messages. Setting only one makes the two
 *   disagree.
 * * **Authorization** -- attaches the access token when signed in.
 * * **Token refresh** -- the API rotates refresh tokens and blacklists the old
 *   one on use. A page that fires several queries at once produces several
 *   simultaneous 401s, so all of them must await *one* shared refresh; racing
 *   refreshes would blacklist the token and sign the user out. See
 *   `refreshPromise` below.
 * * **Error normalisation** -- turns any failure into an `ApiError` carrying the
 *   server's `code`, so the UI can look up a translated message.
 */

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'

import { authSnapshot, useAuthStore } from '@/stores/authStore'
import { currentLanguage } from '@/stores/languageStore'
import type { ApiErrorBody } from '@/types/api'

// Production builds are served by the same Nginx site as the API, so they call
// it same-origin (`/api/v1`). Development falls back to the local runserver.
// An explicitly set VITE_API_BASE_URL -- even an empty one -- always wins.
const configuredBaseUrl: string | undefined = import.meta.env.VITE_API_BASE_URL

export const API_BASE_URL: string = (
  configuredBaseUrl ?? (import.meta.env.PROD ? '' : 'http://127.0.0.1:8010')
).replace(/\/+$/, '')

export const API_ROOT = `${API_BASE_URL}/api/v1`

/** A failure from the API, with the machine-readable code preserved. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fieldErrors: Record<string, string[]>
  readonly requestId?: string
  readonly retryAfterSeconds?: number

  constructor(
    message: string,
    options: {
      status: number
      code: string
      fieldErrors?: Record<string, string[]>
      requestId?: string
      retryAfterSeconds?: number
    },
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = options.status
    this.code = options.code
    this.fieldErrors = options.fieldErrors ?? {}
    this.requestId = options.requestId
    this.retryAfterSeconds = options.retryAfterSeconds
  }

  /** True when the user should be asked to sign in again. */
  get isAuthError(): boolean {
    return this.status === 401
  }
}

const normalizeFieldErrors = (
  errors: ApiErrorBody['errors'],
): Record<string, string[]> => {
  if (!errors) return {}
  return Object.fromEntries(
    Object.entries(errors).map(([field, value]) => [
      field,
      Array.isArray(value) ? value.map(String) : [String(value)],
    ]),
  )
}

const toApiError = (error: AxiosError<ApiErrorBody>): ApiError => {
  if (!error.response) {
    // No response at all: offline, DNS failure, or CORS rejection.
    return new ApiError(error.message || 'Network error', {
      status: 0,
      code: 'network_error',
    })
  }
  const { status, data } = error.response
  return new ApiError(data?.detail || error.message, {
    status,
    code: data?.code || `http_${status}`,
    fieldErrors: normalizeFieldErrors(data?.errors),
    requestId: data?.request_id,
    retryAfterSeconds: data?.retry_after_seconds,
  })
}

export const api: AxiosInstance = axios.create({
  baseURL: API_ROOT,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' },
})

// -- Request: language + credentials ---------------------------------------
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const language = currentLanguage()

  config.params = { lang: language, ...(config.params as object | undefined) }
  config.headers.set('Accept-Language', language)

  const { access } = authSnapshot()
  if (access && !config.headers.has('Authorization')) {
    config.headers.set('Authorization', `Bearer ${access}`)
  }
  return config
})

// -- Response: single-flight refresh, then normalise errors ----------------

/**
 * The in-flight refresh, shared by every request that hits a 401 at once.
 * Resolves to a fresh access token, or null when the session is unrecoverable.
 */
let refreshPromise: Promise<string | null> | null = null

const requestNewAccessToken = async (): Promise<string | null> => {
  const { refresh } = authSnapshot()
  if (!refresh) return null

  try {
    // A bare axios call: using `api` would re-enter these interceptors.
    const response = await axios.post<{ access: string; refresh?: string }>(
      `${API_ROOT}/auth/refresh/`,
      { refresh },
      { headers: { 'Content-Type': 'application/json' }, timeout: 20000 },
    )
    const store = useAuthStore.getState()
    store.setAccess(response.data.access)
    if (response.data.refresh) {
      // Rotation is on, so the old refresh token is now blacklisted -- keep the
      // replacement or the next refresh will fail.
      useAuthStore.setState({ refresh: response.data.refresh })
    }
    return response.data.access
  } catch {
    useAuthStore.getState().clear()
    return null
  }
}

const refreshAccessToken = (): Promise<string | null> => {
  refreshPromise ??= requestNewAccessToken().finally(() => {
    refreshPromise = null
  })
  return refreshPromise
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const config = error.config as RetriableConfig | undefined
    const status = error.response?.status

    const isRefreshCall = config?.url?.includes('/auth/refresh/')
    const canRetry =
      status === 401 && config && !config._retried && !isRefreshCall && Boolean(authSnapshot().refresh)

    if (canRetry) {
      config._retried = true
      const access = await refreshAccessToken()
      if (access) {
        config.headers.set('Authorization', `Bearer ${access}`)
        return api.request(config)
      }
    }

    if (status === 401) {
      // Either there was nothing to refresh with, or the refresh itself failed.
      useAuthStore.getState().clear()
    }

    return Promise.reject(toApiError(error))
  },
)

// -- Thin typed helpers -----------------------------------------------------
export const get = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
  (await api.get<T>(url, config)).data

export const post = async <T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> => (await api.post<T>(url, body, config)).data

export const patch = async <T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> => (await api.patch<T>(url, body, config)).data

export const del = async (url: string, config?: AxiosRequestConfig): Promise<void> => {
  await api.delete(url, config)
}

/**
 * Serialise filter objects the way django-filter expects.
 *
 * Repeated keys for multi-value filters (`status=a&status=b`), booleans as
 * `true`/`false`, and empty values dropped entirely.
 */
export const toQueryParams = (filters: Record<string, unknown>): URLSearchParams => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      value.filter((item) => item !== undefined && item !== null && item !== '').forEach((item) =>
        params.append(key, String(item)),
      )
    } else if (typeof value === 'boolean') {
      params.set(key, value ? 'true' : 'false')
    } else {
      params.set(key, String(value))
    }
  }
  return params
}
